/**
 * Plan/tier arithmetic for storefront templates.
 *
 * Kept as pure functions in their own file because three separate places need
 * the identical answer and must not drift: the merchant list (`isLocked`
 * annotation), `applyToStore` (the authoritative gate), and `/store/config`
 * (`entitlementLapsed`). The first and third are UX; only the second is
 * security — but they have to agree or the picker lies.
 */

import { StorefrontTemplateTier } from '@cart/shared-types';

/** Ordered low → high. Mirrors the CHECK constraint in migration 030. */
export const STOREFRONT_TEMPLATE_TIERS: readonly StorefrontTemplateTier[] = ['free', 'basic', 'premium'];

/** The template every unset / unpublished / unknown resolution falls back to. */
export const DEFAULT_STOREFRONT_TEMPLATE_SLUG = 'classic';

const RANK: Record<string, number> = { free: 0, basic: 1, premium: 2 };

export function isStorefrontTemplateTier(value: string): value is StorefrontTemplateTier {
  return (STOREFRONT_TEMPLATE_TIERS as readonly string[]).includes(value);
}

/**
 * An unrecognised plan ranks as `free` — the least privilege we can assume.
 * (`Organization.plan` is a plain string, so an unmapped value is possible.)
 */
export function planRank(plan: string): number {
  return RANK[plan] ?? 0;
}

/**
 * An unrecognised tier ranks above every plan, i.e. locked to everyone. This
 * asymmetry with `planRank` is deliberate and fail-closed: if a future tier is
 * added to the DB before the code knows about it, nobody gets it by accident.
 */
export function tierRank(tier: string): number {
  return RANK[tier] ?? Number.MAX_SAFE_INTEGER;
}

/**
 * The plan an organisation is *actually* entitled to right now. An expired
 * `planExpiresAt` collapses to `free`; a null expiry means the plan does not
 * expire, which is how every pre-billing org is stored.
 */
export function effectivePlan(
  plan: string | null | undefined,
  planExpiresAt: Date | null | undefined,
  now: Date = new Date(),
): string {
  if (!plan) return 'free';
  if (planExpiresAt && planExpiresAt.getTime() <= now.getTime()) return 'free';
  return plan;
}

export function planCoversTier(plan: string, tier: string): boolean {
  return planRank(plan) >= tierRank(tier);
}
